import $ from 'jquery';
import BaseComponent from './baseComponent';

/**
 * Search through content
 */
export default class HypoCalculator extends BaseComponent {
  /**
     * Public constructor
     *
     * @param {object} options
     */
  constructor(options) {
    super(options);

    // settings
    this.settings = {
      rateFactor: 0.045,
      expenseFactor: 0.0075,
      maxNetIncomePart: 0.4,
      yearlyAmortisationFactor: 0.67,
    };

    // calc variables from input
    this.location = 15;
    this.price = 0;
    this.equity = 0;
    this.netIncome = 0;

    // calc variables to output
    this.viability = 0;
    this.viabilityPositive = true;
    this.requiredNetIncome = 0;
    this.monthlyRate = 0;

    // input
    this.$element = null;
    this.$inputElements = {
      location: null, price: null, equity: null, netIncome: null,
    };
    this.$outputElements = {
      viabilityFill: null,
      viabilityKnob: null,
      viabilityToggle: null,
      requiredNetIncome: null,
      monthlyRate: null,
      step: [],
    };

    return this;
  }

  /**
     * Initialize function
     */
  init() {
    this.$element = $('[data-hypo]');

    if (this.$element.length > 0) {
      this.findElements(this.$inputElements, true);
      this.findElements(this.$outputElements, false);
      this.calculate();
    }
  }

  /**
     * Original calculation from old website -> require dev, remove when tested
     */
  originalCalculation() {
    const Kaufpreis = this.price;
    const Eigenmittel = this.equity;
    const Nettoeinkommen = this.netIncome;
    const AmortisationFaktor = this.location;
    let Hypothekarbetrag = Kaufpreis - Eigenmittel;
    let Zins = Hypothekarbetrag * 0.045;
    let Nebenkosten = Kaufpreis * 0.0075;

    let Amortisation = HypoCalculator.refactor(
      ((((Hypothekarbetrag / Kaufpreis) * Kaufpreis) - (0.67 * Kaufpreis)) / AmortisationFaktor),
      100,
    );

    if (Amortisation <= 0) Amortisation = 0;

    let Belastung = Zins + Nebenkosten + Amortisation;
    let Tragbarkeit = (100 / Nettoeinkommen) * Belastung;
    let BenNetto = HypoCalculator.refactor((Belastung / 0.4), 1000);
    let MonBelastung = (Belastung / 12).toFixed(0);

    if (Eigenmittel >= Kaufpreis) {
      Hypothekarbetrag = 0;
      Zins = 0;
      Nebenkosten = 0;
      Amortisation = 0;
      Belastung = 0;
      Tragbarkeit = 0;
      BenNetto = 0;
      MonBelastung = 0;
    }

    return {
      requiredNetIncome: BenNetto,
      viability: Tragbarkeit,
      monthlyRate: MonBelastung,
      repayment: Amortisation,
    };
  }

  /**
     * Calculates the hypo
     */
  calculate() {
    this.updateInputs();

    const mortage = this.price - this.equity;
    const rate = mortage * this.settings.rateFactor;
    const expenses = this.price * this.settings.expenseFactor;
    const repaymentFactor = this.location;

    let repayment = HypoCalculator.refactor(
      ((((mortage / this.price) * this.price)
        - (this.settings.yearlyAmortisationFactor * this.price))
      / repaymentFactor),
      100,
    );
    repayment = Math.max(0, repayment);

    const burden = rate + expenses + repayment;

    this.viability = (100 / this.netIncome) * burden;
    this.requiredNetIncome = HypoCalculator.refactor(
      (burden / this.settings.maxNetIncomePart), 1000,
    );

    this.monthlyRate = (burden / 12).toFixed(0);

    if (this.equity >= this.price) {
      this.viability = 0;
      this.requiredNetIncome = 0;
      this.monthlyRate = 0;
    }

    this.viabilityPositive = this.viability <= 40;

    this.updateOutputs();
  }

  /**
     * Retrieves data from all inputs
     */
  updateInputs() {
    Object.keys(this.$inputElements).forEach((elementKey) => {
      if (this.$inputElements[elementKey].length > 1) {
        this[elementKey] = parseInt(this.$inputElements[elementKey].filter(':checked').val(), 10);
      } else {
        this[elementKey] = parseInt(this.$inputElements[elementKey].val(), 10);
      }
    });
  }

  /**
     * Updates the outputs
     */
  updateOutputs() {
    let viabilityPercentage = Math.min(100, this.viability);
    viabilityPercentage = Math.max(0, viabilityPercentage);
    this.$outputElements.viabilityFill.css('width', `${viabilityPercentage}%`);
    this.$outputElements.viabilityKnob.css('left', `${viabilityPercentage}%`);
    this.$outputElements.monthlyRate.text(HypoCalculator.formatNumber(this.monthlyRate));
    this.$outputElements.requiredNetIncome.text(
      HypoCalculator.formatNumber(this.requiredNetIncome),
    );

    if (this.viabilityPositive) {
      this.$outputElements.viabilityToggle.removeClass('-negative');
    } else {
      this.$outputElements.viabilityToggle.addClass('-negative');
    }

    $.each(this.$outputElements.step, (index, step) => {
      if (this.price >= Number($(step).data('hypo-step-price'))) {
        $(step).addClass('-visible');
      } else {
        $(step).removeClass('-visible');
      }
    });
  }

  /**
     * Finds elements by obj keys. adds change-listener (optional)
     *
     * @param {number} obj
     * @param {bool} addChangeListener
     */
  findElements(obj, addChangeListener) {
    const elementList = obj;
    const self = this;
    $.each(elementList,
      (key) => {
        elementList[key] = this.$element.find(`[data-hypo-${key}]`);
        if (addChangeListener) {
          elementList[key].on(elementList[key].is('input:radio') ? 'change' : 'input', (event) => {
            self.calculate();
            const input = $(event.target);
            if (input.data('hypo-output')) {
              $(`[${input.data('hypo-output')}]`).text(
                HypoCalculator.formatNumber(input.val()),
              );
            }
          });
        }
      });
  }

  /**
     * Calculates the repayment
     *
     * @param {number} mortgage
     * @param {number} repaymentFactor
     * @returns {number}
     */
  calculateRepayment(mortage, repaymentFactor) {
    return Math.max(
      0,
      HypoCalculator.refactor(
        ((((mortage / this.price) * this.price)
            - (this.settings.yearlyAmortisationFactor * this.price))
        / repaymentFactor),
        100,
      ),
    );
  }

  /**
     * Does refactor
     *
     * @param {number} sum
     * @param {number} factor
     * @returns {number}
     */
  static refactor(sum, factor) {
    return Number(
      (sum / factor).toFixed(0) * factor,
    );
  }

  /**
     * Formats number with quotes
     *
     * @param {number} x
     * @returns {string}
     */
  static formatNumber(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  }
}
